import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

export default function Tacho() {
  const [compliance, setCompliance] = useState<any>(null);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [recentUploads, setRecentUploads] = useState<any[]>([]);
  const [uploadType, setUploadType] = useState<'driver_card' | 'vehicle_unit'>('driver_card');
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTachoData();
  }, []);

  const loadTachoData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [complianceRes, downloadsRes, usersRes, vehiclesRes] = await Promise.all([
        axios.get(`${API_BASE}/tacho/downloads/compliance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/tacho/downloads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE}/vehicles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setCompliance(complianceRes.data.data);
      setRecentUploads(downloadsRes.data.data || []);
      const allUsers = usersRes.data.data || [];
      setDrivers(allUsers.filter((u: any) => u.role === 'driver'));
      setVehicles(vehiclesRes.data.data || []);
    } catch (error) {
      console.error('Error loading tacho data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    if (uploadType === 'driver_card' && !selectedDriverId) return;
    if (uploadType === 'vehicle_unit' && !selectedVehicleId) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('download_type', uploadType);
      if (uploadType === 'driver_card') formData.append('driver_id', selectedDriverId);
      if (uploadType === 'vehicle_unit') formData.append('vehicle_id', selectedVehicleId);

      await axios.post(`${API_BASE}/tacho/downloads`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFile(null);
      await loadTachoData();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mb-4"></div>
        <p className="text-gray-600">Loading tacho data...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Tacho</h1>

      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Upload DDD Download</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Upload Type</label>
            <select
              value={uploadType}
              onChange={(e) => {
                const v = e.target.value as 'driver_card' | 'vehicle_unit';
                setUploadType(v);
                setFile(null);
              }}
              className="w-full border rounded px-3 py-2"
            >
              <option value="driver_card">Driver card (28 days)</option>
              <option value="vehicle_unit">Vehicle unit (90 days)</option>
            </select>
          </div>

          <div>
            {uploadType === 'driver_card' ? (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-1">Driver</label>
                <select
                  value={selectedDriverId}
                  onChange={(e) => setSelectedDriverId(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select driver</option>
                  {drivers.map((d) => (
                    <option key={d.id} value={String(d.id)}>{d.name}</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle</label>
                <select
                  value={selectedVehicleId}
                  onChange={(e) => setSelectedVehicleId(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select vehicle</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={String(v.id)}>{v.registration_number}</option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">DDD File</label>
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full"
          />
        </div>

        <button
          onClick={handleUpload}
          disabled={submitting || !file || (uploadType === 'driver_card' && !selectedDriverId) || (uploadType === 'vehicle_unit' && !selectedVehicleId)}
          className={`mt-4 px-4 py-2 rounded-lg text-white transition-colors ${
            submitting || !file || (uploadType === 'driver_card' && !selectedDriverId) || (uploadType === 'vehicle_unit' && !selectedVehicleId)
              ? 'bg-gray-400'
              : 'bg-gray-800 hover:bg-gray-900'
          }`}
        >
          {submitting ? 'Uploading...' : 'Upload'}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Recent uploads</h2>
        {recentUploads.length === 0 ? (
          <p className="text-gray-500">No uploads yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Driver</th>
                  <th className="py-2 pr-4">Vehicle</th>
                  <th className="py-2 pr-4">Filename</th>
                  <th className="py-2 pr-4">Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {recentUploads.slice(0, 20).map((u: any) => (
                  <tr key={u.id} className="border-b last:border-b-0">
                    <td className="py-2 pr-4 text-gray-800">
                      {u.download_type === 'driver_card' ? 'Driver card' : 'Vehicle unit'}
                    </td>
                    <td className="py-2 pr-4 text-gray-800">{u.driver_name || '-'}</td>
                    <td className="py-2 pr-4 text-gray-800">{u.vehicle_registration || '-'}</td>
                    <td className="py-2 pr-4 text-gray-600">{u.original_filename || '-'}</td>
                    <td className="py-2 pr-4 text-gray-600">{u.uploaded_at ? new Date(u.uploaded_at).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Compliance</h2>
        {!compliance ? (
          <p className="text-gray-500">No compliance data available</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Driver Card Downloads</h3>
              <p className="text-sm text-gray-600 mb-3">Overdue: <span className="font-bold">{compliance.drivers.overdue.length}</span> • Due soon: <span className="font-bold">{compliance.drivers.dueSoon.length}</span></p>
              <div className="space-y-2">
                {(compliance.drivers.all || []).map((d: any) => {
                  const status = d.overdue ? 'overdue' : d.dueSoon ? 'due_soon' : 'ok';
                  const border = status === 'overdue' ? 'border-red-500' : status === 'due_soon' ? 'border-orange-500' : 'border-green-500';
                  const badge = status === 'overdue'
                    ? 'bg-red-100 text-red-800'
                    : status === 'due_soon'
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-green-100 text-green-800';
                  const badgeText = status === 'overdue' ? 'Overdue' : status === 'due_soon' ? 'Due soon' : 'OK';

                  return (
                    <div key={d.id} className={`border-l-4 ${border} pl-3 py-2 flex items-start justify-between gap-3`}>
                      <div>
                        <p className="font-medium text-gray-800">{d.name}</p>
                        <p className="text-xs text-gray-500">
                          Last upload: {d.last_upload ? new Date(d.last_upload).toLocaleDateString() : 'Never'}
                          {d.days_until_due !== null && d.days_until_due !== undefined ? ` • Due in ${d.days_until_due} day(s)` : ''}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge}`}>{badgeText}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Vehicle Unit Downloads</h3>
              <p className="text-sm text-gray-600 mb-3">Overdue: <span className="font-bold">{compliance.vehicles.overdue.length}</span> • Due soon: <span className="font-bold">{compliance.vehicles.dueSoon.length}</span></p>
              <div className="space-y-2">
                {(compliance.vehicles.all || []).map((v: any) => {
                  const status = v.overdue ? 'overdue' : v.dueSoon ? 'due_soon' : 'ok';
                  const border = status === 'overdue' ? 'border-red-500' : status === 'due_soon' ? 'border-orange-500' : 'border-green-500';
                  const badge = status === 'overdue'
                    ? 'bg-red-100 text-red-800'
                    : status === 'due_soon'
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-green-100 text-green-800';
                  const badgeText = status === 'overdue' ? 'Overdue' : status === 'due_soon' ? 'Due soon' : 'OK';

                  return (
                    <div key={v.id} className={`border-l-4 ${border} pl-3 py-2 flex items-start justify-between gap-3`}>
                      <div>
                        <p className="font-medium text-gray-800">{v.registration_number}</p>
                        <p className="text-xs text-gray-500">
                          Last upload: {v.last_upload ? new Date(v.last_upload).toLocaleDateString() : 'Never'}
                          {v.days_until_due !== null && v.days_until_due !== undefined ? ` • Due in ${v.days_until_due} day(s)` : ''}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge}`}>{badgeText}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
